import { ENV } from '@/lib/env';
import { getAccessToken } from './auth';

export interface Workspace {
  code: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  data: {
    image_url?: string;
    entitlements?: string[];
  };
  updated_at: number;
}

export interface WorkspaceInput {
  code: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  data: {
    image_url?: string;
    entitlements?: string[];
  };
}

export interface WorkspaceFormData {
  code: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  image_url: string;
  entitlements: string[];
}

export interface GetWorkspacesOutput {
  workspaces: Workspace[];
}

export interface UpsertWorkspaceInput {
  input_model: {
    workspace: {
      code: string;
      name: string;
      description: string;
      status: 'active' | 'inactive';
      data: {
        image_url?: string;
        entitlements?: string[];
      };
    };
  };
}

export interface UpsertWorkspaceOutput {
  success: boolean;
  workspace: Workspace;
  error?: string;
}

export interface UserWorkspace {
  code: string;
  name: string;
  description: string;
  created_at: number;
  updated_at: number;
  status: 'active' | 'inactive';
  owner_id: string;
  data: {
    entitlements?: string[];
  };
}

export interface GetUserWorkspacesOutput {
  workspaces: UserWorkspace[];
}

/**
 * Get all workspaces
 */
export async function getAllWorkspaces(): Promise<Workspace[]> {
  const apiUrl = ENV.BACKEND_API;
  const url = `${apiUrl}/modules/trigger/dev/project_manager-workspace/get_all_workspaces`;
  
  const token = getAccessToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('input_model', JSON.stringify({
    user_id: '',
    request_timestamp: 0
  }));
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch workspaces');
  }
  
  const data: GetWorkspacesOutput = await response.json();
  return data.workspaces;
}

/**
 * Create or update a workspace
 */
export async function upsertWorkspace(input: UpsertWorkspaceInput): Promise<Workspace> {
  const apiUrl = ENV.BACKEND_API;
  const url = `${apiUrl}/modules/trigger/dev/project_manager-workspace/upsert_workspace`;
  
  const token = getAccessToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('input_model', JSON.stringify(input.input_model));
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });
  
  if (!response.ok) {
    throw new Error('Failed to upsert workspace');
  }
  
  return response.json();
}

/**
 * Get user's accessible workspaces
 */
export async function getUserWorkspaces(): Promise<UserWorkspace[]> {
  const apiUrl = ENV.BACKEND_API;
  const url = `${apiUrl}/modules/trigger/dev/project_manager-workspace/get_user_workspaces`;
  
  const token = getAccessToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const formData = new FormData();
  formData.append('input_model', JSON.stringify({}));
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch user workspaces');
  }
  
  const data: GetUserWorkspacesOutput = await response.json();
  return data.workspaces || [];
} 